import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { MatDialogRef, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmDialog, PromptDialog, MultiPromptDialog } from '../components/dialogs';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ComponentType } from '@angular/cdk/portal';
import { Observable, firstValueFrom } from 'rxjs';

@Injectable()
export class DialogService {
    constructor(
        private dialog: MatDialog,
        private router: Router,
        private translate: TranslateService,
    ) {
        this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
            this.closeOpenDialogs();
        });
    }

    closeOpenDialogs() {
        this.dialog.closeAll();
    }

    open<T, D = any, R = any>(component: ComponentType<T>, options: MatDialogConfig<D>): MatDialogRef<T, R> {
        return this.dialog.open<T, D, R>(component, options);
    }

    confirm<T extends any = boolean>(title: string, message: string): Observable<T>;
    confirm<T extends any = boolean>(
        title: string,
        message: string,
        toPromise: true,
        buttons?: Array<{ label: string; value: any }>,
    ): Promise<T>;
    confirm<T extends any = boolean>(
        title: string,
        message: string,
        toPromise: false,
        buttons?: Array<{ label: string; value: any }>,
    ): Observable<T>;
    confirm<T extends any = boolean>(
        title: string,
        message: string,
        toPromise: boolean = false,
        buttons: Array<{ label: string; value: any }> = [
            { label: 'Avbryt', value: false },
            { label: 'OK', value: true },
        ],
    ): Promise<T> | Observable<T> {
        let dialogRef: MatDialogRef<ConfirmDialog, T>;

        dialogRef = this.open(ConfirmDialog, {
            disableClose: true,
            minWidth: 300,
            data: {
                buttons: buttons,
            },
        });

        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.message = message;

        if (toPromise) {
            return firstValueFrom<T>(dialogRef.afterClosed()) as Promise<T>;
        }

        return dialogRef.afterClosed() as Observable<T>;
    }

    prompt<T = string>(title: string, label: string, value: string, toPromise: true): Promise<T>;
    prompt<T = string>(title: string, label: string, value: string, toPromise: false): Observable<T>;
    prompt<T = string>(title: string, label: string, value: string): Observable<T>;
    prompt<T = string>(title: string, label: string, value: string, toPromise: boolean = false): Promise<T> | Observable<T> {
        let dialogRef: MatDialogRef<PromptDialog, T>;

        dialogRef = this.open(PromptDialog, {
            disableClose: true,
            minWidth: 300,
            maxWidth: 500,
        });

        dialogRef.componentInstance.title = this.translate.instant(title);
        dialogRef.componentInstance.label = this.translate.instant(label);
        dialogRef.componentInstance.value = value;

        if (toPromise) {
            return firstValueFrom(dialogRef.afterClosed());
        }

        return dialogRef.afterClosed();
    }

    multiPrompt<T = string[]>(title: string, fields: { value: string; label: string; key?: string }[], toPromise: true): Promise<T>;
    multiPrompt<T = string[]>(title: string, fields: { value: string; label: string; key?: string }[], toPromise: false): Observable<T>;
    multiPrompt<T = string[]>(title: string, fields: { value: string; label: string; key?: string }[]): Observable<T>;
    multiPrompt<T = string[]>(
        title: string,
        fields: { value: string; label: string; key?: string }[],
        toPromise: boolean = false,
    ): Promise<T> | Observable<T> {
        let dialogRef: MatDialogRef<MultiPromptDialog, T>;

        dialogRef = this.dialog.open(MultiPromptDialog, {
            disableClose: true,
            minWidth: 300,
            maxWidth: 500,
        });

        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.fields = fields;

        if (toPromise) {
            return firstValueFrom(dialogRef.afterClosed());
        }

        return dialogRef.afterClosed();
    }
}
